import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { Scan } from '../types/scan';
import { triggerAbandonedScanEmail } from '../services/emailTriggerService';
export function useScans(userId: string | undefined) {
  const [scans, setScans] = useState<Scan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    fetchScans();
    const unsubscribe = subscribeToScans();
    return unsubscribe;
  }, [userId]);
  const fetchScans = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('scans').select('*').eq('user_id', userId).order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setScans(data || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const subscribeToScans = () => {
    const channel = supabase.channel('scans-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'scans',
      filter: `user_id=eq.${userId}`
    }, payload => {
      console.log('[useScans] Realtime event:', payload.eventType);
      if (payload.eventType === 'INSERT') {
        setScans(prev => [payload.new as Scan, ...prev]);
      } else if (payload.eventType === 'UPDATE') {
        setScans(prev => prev.map(scan => scan.id === payload.new.id ? payload.new as Scan : scan));
      } else if (payload.eventType === 'DELETE') {
        setScans(prev => prev.filter(scan => scan.id !== payload.old.id));
      }
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  };
  const refetch = () => {
    setLoading(true);
    fetchScans();
  };
  return {
    scans,
    loading,
    error,
    refetch
  };
}
export interface ScanStatusChange {
  previousStatus: Scan['status'] | null;
  currentStatus: Scan['status'];
  scan: Scan;
}
export function useScan(scanId: string | undefined) {
  const [scan, setScan] = useState<Scan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('disconnected');
  const [statusChange, setStatusChange] = useState<ScanStatusChange | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const channelRef = useRef<any>(null);
  const previousStatusRef = useRef<Scan['status'] | null>(null);
  const mountedRef = useRef<boolean>(true);
  const abandonedScanTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Helper to normalize scan data
  const normalizeScan = (data: any): Scan => ({
    ...data,
    is_unlocked: data.is_unlocked === true
  });
  // Helper to detect and report status changes
  const handleStatusChange = (newScan: Scan) => {
    const prevStatus = previousStatusRef.current;
    const newStatus = newScan.status;
    // Detect meaningful status transitions
    if (prevStatus && prevStatus !== newStatus) {
      console.log(`[useScan] Status changed: ${prevStatus} → ${newStatus}`, {
        scanId: newScan.id,
        score: newScan.score,
        riskLevel: newScan.risk_level
      });
      // Only report completion or failure transitions
      if (newStatus === 'completed' || newStatus === 'failed') {
        setStatusChange({
          previousStatus: prevStatus,
          currentStatus: newStatus,
          scan: newScan
        });
      }
    }
    previousStatusRef.current = newStatus;
  };
  const fetchScan = async () => {
    if (!scanId || !mountedRef.current) return null;
    try {
      console.log('[useScan] Fetching scan:', scanId);
      const {
        data,
        error: fetchError
      } = await supabase.from('scans').select('*').eq('id', scanId).single();
      if (fetchError) throw fetchError;
      if (!mountedRef.current) return null;
      console.log('[useScan] Fetch result:', {
        id: data?.id,
        status: data?.status,
        is_unlocked: data?.is_unlocked,
        score: data?.score,
        risk_level: data?.risk_level
      });
      const normalizedData = normalizeScan(data);
      handleStatusChange(normalizedData);
      setScan(normalizedData);
      setError(null);
      return normalizedData;
    } catch (err: any) {
      console.error('[useScan] Fetch error:', err.message);
      if (mountedRef.current) {
        setError(err.message);
        setScan(null);
      }
      return null;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };
  // Start polling as fallback when scan is processing
  const startPolling = () => {
    if (pollingIntervalRef.current) return; // Already polling
    console.log('[useScan] Starting polling fallback for scan:', scanId);
    pollingIntervalRef.current = setInterval(async () => {
      if (!mountedRef.current) {
        stopPolling();
        return;
      }
      console.log('[useScan] Polling tick for scan:', scanId);
      const data = await fetchScan();
      // Stop polling once scan is completed or failed
      if (data && (data.status === 'completed' || data.status === 'failed')) {
        console.log('[useScan] Scan completed/failed, stopping polling. Status:', data.status, 'score:', data.score, 'is_unlocked:', data.is_unlocked);
        stopPolling();
      }
    }, 2000); // Poll every 2 seconds
  };
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      console.log('[useScan] Stopping polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };
  // Clear status change after it's been consumed
  const clearStatusChange = () => {
    setStatusChange(null);
  };
  // Schedule abandoned scan email when user hits paywall (completed but not unlocked)
  const scheduleAbandonedScanEmail = async (completedScan: Scan) => {
    // Only schedule if scan is completed but locked (paywall state)
    if (completedScan.status !== 'completed' || completedScan.is_unlocked) {
      return;
    }
    // Clear any existing timer
    if (abandonedScanTimerRef.current) {
      clearTimeout(abandonedScanTimerRef.current);
      abandonedScanTimerRef.current = null;
    }
    console.log('[useScan] Scheduling abandoned scan email for scan:', completedScan.id);
    // Schedule email after 2 hours
    abandonedScanTimerRef.current = setTimeout(async () => {
      if (!mountedRef.current) return;
      try {
        // Re-fetch scan to check current state
        const {
          data: currentScan,
          error: fetchError
        } = await supabase.from('scans').select('*').eq('id', completedScan.id).single();
        if (fetchError || !currentScan) {
          console.error('[useScan] Failed to re-fetch scan for abandoned email:', fetchError);
          return;
        }
        // Guard: only send if still locked and email not already sent
        if (currentScan.is_unlocked) {
          console.log('[useScan] Scan already unlocked, skipping abandoned email');
          return;
        }
        if (currentScan.abandoned_email_sent) {
          console.log('[useScan] Abandoned email already sent, skipping');
          return;
        }
        // Set flag to prevent duplicates
        const {
          error: updateError
        } = await supabase.from('scans').update({
          abandoned_email_sent: true
        }).eq('id', completedScan.id);
        if (updateError) {
          console.error('[useScan] Failed to set abandoned_email_sent flag:', updateError);
          return;
        }
        // Trigger the abandoned scan email
        const reportLink = `${window.location.origin}/results/${completedScan.id}`;
        const result = await triggerAbandonedScanEmail(reportLink, {
          scanTitle: completedScan.title,
          expiresInHours: 2
        });
        if (result.success) {
          console.log('[useScan] Abandoned scan email sent successfully');
        } else {
          console.error('[useScan] Abandoned scan email failed:', result.error);
          // Revert flag on failure so it can be retried
          await supabase.from('scans').update({
            abandoned_email_sent: false
          }).eq('id', completedScan.id);
        }
      } catch (err) {
        console.error('[useScan] Error in abandoned scan email trigger:', err);
      }
    }, 1000 * 60 * 60 * 2); // 2 hours
  };
  useEffect(() => {
    mountedRef.current = true;
    if (!scanId) {
      setLoading(false);
      return;
    }
    console.log('[useScan] Initializing for scan:', scanId);
    // Reset previous status ref when scanId changes
    previousStatusRef.current = null;
    // Initial fetch
    fetchScan().then(data => {
      // If scan is still processing, start polling as backup
      if (data && (data.status === 'pending' || data.status === 'processing')) {
        startPolling();
      }
    });
    // Set up realtime subscription with proper status handling
    const channel = supabase.channel(`scan-${scanId}`).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'scans',
      filter: `id=eq.${scanId}`
    }, payload => {
      if (!mountedRef.current) return;
      console.log('[useScan] Realtime UPDATE received:', {
        id: payload.new.id,
        status: payload.new.status,
        is_unlocked: payload.new.is_unlocked,
        score: payload.new.score,
        risk_level: payload.new.risk_level
      });
      const newScan = normalizeScan(payload.new);
      console.log('[useScan] Setting scan state with normalized data:', {
        status: newScan.status,
        is_unlocked: newScan.is_unlocked,
        score: newScan.score
      });
      handleStatusChange(newScan);
      setScan(newScan);
      // Stop polling if scan completed
      if (newScan.status === 'completed' || newScan.status === 'failed') {
        stopPolling();
      }
    }).subscribe((status, err) => {
      if (!mountedRef.current) return;
      console.log('[useScan] Subscription status:', status, err);
      setSubscriptionStatus(status);
      if (status === 'SUBSCRIBED') {
        console.log('[useScan] Successfully subscribed to realtime updates');
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.warn('[useScan] Realtime subscription failed, relying on polling');
        // Ensure polling is running as fallback
        startPolling();
      }
    });
    channelRef.current = channel;
    return () => {
      mountedRef.current = false;
      stopPolling();
      // Clear abandoned scan timer on cleanup
      if (abandonedScanTimerRef.current) {
        clearTimeout(abandonedScanTimerRef.current);
        abandonedScanTimerRef.current = null;
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [scanId]);
  // Schedule abandoned scan email when scan completes but is locked
  useEffect(() => {
    if (scan && scan.status === 'completed' && !scan.is_unlocked) {
      scheduleAbandonedScanEmail(scan);
    }
    // If scan gets unlocked, cancel the timer
    if (scan && scan.is_unlocked && abandonedScanTimerRef.current) {
      console.log('[useScan] Scan unlocked, cancelling abandoned email timer');
      clearTimeout(abandonedScanTimerRef.current);
      abandonedScanTimerRef.current = null;
    }
  }, [scan?.status, scan?.is_unlocked]);
  // Also start polling if scan is processing and we don't have realtime
  useEffect(() => {
    if (!mountedRef.current) return;
    if (scan && (scan.status === 'pending' || scan.status === 'processing')) {
      if (subscriptionStatus !== 'SUBSCRIBED') {
        startPolling();
      }
    }
  }, [scan?.status, subscriptionStatus]);
  return {
    scan,
    loading,
    error,
    refetch: fetchScan,
    subscriptionStatus,
    statusChange,
    clearStatusChange
  };
}
export async function createScan(userId: string, title: string, fileUrls: string[], conversationText?: string, mode?: string): Promise<string> {
  const {
    data,
    error
  } = await supabase.from('scans').insert({
    user_id: userId,
    title,
    file_urls: fileUrls,
    conversation_text: conversationText || null,
    mode: mode || 'default',
    status: 'pending',
    created_at: new Date().toISOString()
  }).select().single();
  if (error) throw error;
  return data.id;
}
export async function updateScanAnalysis(scanId: string, analysis: {
  score: number;
  risk_level: 'low' | 'medium' | 'high';
  message_count: number;
  patterns: any[];
}): Promise<void> {
  // Get scan details first
  const {
    data: scan,
    error: fetchError
  } = await supabase.from('scans').select('*, user_id, title').eq('id', scanId).single();
  if (fetchError) throw fetchError;
  // Update scan with analysis results
  const {
    error
  } = await supabase.from('scans').update({
    status: 'completed',
    score: analysis.score,
    risk_level: analysis.risk_level,
    message_count: analysis.message_count,
    patterns: analysis.patterns,
    updated_at: new Date().toISOString()
  }).eq('id', scanId);
  if (error) throw error;
  // Email notification is handled automatically by the database trigger
  // which inserts a 'toxicityReport' row into email_logs when a scan completes.
  // No frontend email send needed here.
}
export async function retryScan(scanId: string): Promise<void> {
  const {
    error
  } = await supabase.from('scans').update({
    status: 'pending',
    updated_at: new Date().toISOString()
  }).eq('id', scanId);
  if (error) throw error;
}
export async function deleteScan(scanId: string): Promise<void> {
  const {
    error
  } = await supabase.from('scans').delete().eq('id', scanId);
  if (error) throw error;
}